import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface ICategoryCard {
  label: string;
  iconColor?: string;
  iconKey: IconKeyType;
  analyticsId: string;
  customAttributes: Record<string, string> | null;
  onPress: () => void;
  isDisabled: boolean;
}

const CategoryCard = ({
  label,
  iconKey,
  iconColor,
  isDisabled = false,
  onPress,
  analyticsId = '',
  customAttributes = null,
}: ICategoryCard) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  return (
    <PressableWithDelay
      style={[
        styles.cardContainer,
        isFocused ? styles.focus : null,
        isHovered ? styles.hover : null,
        isPressed ? styles.press : null,
      ]}
      onPress={() => {
        cardPressAnalytics();
        onPress?.();
        if (isWeb) {
          setIsFocused(false);
        }
      }}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      role="button"
    >
      <View style={styles.mainContainer} testID={label}>
        <Icon
          iconKey={iconKey}
          iconColor={isDisabled ? theme.color.text.muted : iconColor}
        />
        <BodySmBoldText
          style={[styles.text, isDisabled ? styles.disabledText : null]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {label}
        </BodySmBoldText>
      </View>
    </PressableWithDelay>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'flex-start',
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    flexBasis: '48%',
    flexGrow: 1,
  },
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    borderColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  mainContainer: {
    flex: 1,
    gap: theme.spacing.s,
    minWidth: 0,
    padding: theme.spacing.m,
    width: '100%',
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  text: {
    flex: 1,
  },
});

export default CategoryCard;
